import { AfterViewInit, Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgxPlaidLinkService, PlaidConfig, PlaidCreateConfig, PlaidEventMetadata, PlaidLinkHandler, PlaidOnEventArgs, PlaidOnSuccessArgs, PlaidSuccessMetadata } from 'ngx-plaid-link';
import {
	CreateLinkTokenRequest,
	GetUserIdentityVerificationRequest,
	IdentityVerificationItemResponse,
	ListUserIdentityVerificationRequest,
} from '../../../../../../goldstar-share/src/app/api-data/ng-openapi-gen-next/models';
import { lastValueFrom } from 'rxjs';
import { ApiService } from '../../../api-data/ng-openapi-gen-next/services';
import { PlaidComponentConfig, PlaidUserConfig, UserIdentityVerificationStatus } from '../../../models/models';
import { ErrorHandlingService } from '../../../services/error-handling.service';

@Component({
	selector: 'app-plaid-auth',
	templateUrl: './plaid-auth.component.html',
	styleUrls: ['./plaid-auth.component.scss'],
})
export class PlaidAuthComponent implements OnInit, AfterViewInit {
	private plaidLinkHandler!: PlaidLinkHandler;
	public isPlaidLinkReady: boolean = false;
	public userIdentityVerificationStatus!: UserIdentityVerificationStatus;

	@Input() plaidEnv: string = 'sandbox';
	@Input() config!: PlaidComponentConfig;
	@Input() label!: string;
	@Input() templateId!: string;
	@Input() cultureCode: string = 'en';
	@Output() plaidOutput = new EventEmitter<IdentityVerificationItemResponse>();

	public clientConfig!: PlaidCreateConfig;
	public fetchTokenString!: string;
	public plaidApiConfig!: PlaidApiConfig;
	private plaidClientUserID!: string;

	constructor(
		private plaidLinkService: NgxPlaidLinkService,
		private apiV2: ApiService,
		private errorHandlingService: ErrorHandlingService
	) {}

	ngOnInit(): void {
		this.isPlaidLinkReady = false;
	}

	/**
	 * NgAfterViewInit is only called the first time the component is initialized. For re-triggering plaid the second time, reInitializePlaid hook is called
	 */
	public async ngAfterViewInit(): Promise<void> {
		if (this.config) {
			await this.initializePlaid();
		}
	}

	/**
	 * NgAfterViewInit is only called the first time the component is initialized. For re-triggering plaid the second time, reInitializePlaid hook is called
	 */
	public async reInitializePlaid(): Promise<void> {
		if (this.config) {
			await this.initializePlaid();
		}
	}

	private async initializePlaid(): Promise<void> {
		try {
			const linkTokenRequest = this.config.prepareLinkTokenRequest();
			this.plaidClientUserID = linkTokenRequest.user?.userId ?? '';
			const linkToken = await this.fetchLinkToken(linkTokenRequest);
			this.clientConfig = {
				apiVersion: 'v2',
				env: this.plaidEnv,
				product: linkTokenRequest.products,
				token: linkToken,
				onSuccess: (token: PlaidOnSuccessArgs, metadata: PlaidSuccessMetadata) => this.onSuccess(token, metadata),
				onExit: (error: any, metadata: any) => this.onExit(error, metadata),
				onEvent: (eventArgs: PlaidOnEventArgs, metadata: PlaidEventMetadata) => this.onEvent(eventArgs, metadata),
			};
			this.plaidLinkService
				.createPlaid(this.clientConfig)
				.then((handler: PlaidLinkHandler) => {
					this.plaidLinkHandler = handler;
					this.isPlaidLinkReady = true;
					this.plaidLinkHandler.open();
				})
				.catch((error: any) => {
					throw error;
				});
		} catch (error: any) {
			console.log('Error initializing Plaid');
			this.errorHandlingService.showErrorMessage('Error initializing Plaid');
		}
	}

	private async fetchLinkToken(linkTokenRequest: CreateLinkTokenRequest): Promise<string> {
		try {
			if (!this.config) throw new Error('Plaid config is not initialized');
			const tokenResponse = await lastValueFrom(this.apiV2.acquireLinkToken({ body: linkTokenRequest }));
			if (!tokenResponse.isSuccess || !tokenResponse.data?.token) {
				throw new Error('Failed to fetch plaid token response');
			}
			return tokenResponse.data.token;
		} catch (error: any) {
			throw error;
		}
	}

	exit() {
		this.plaidLinkHandler.exit();
	}

	createUserIdentityVerification() {}

	async onSuccess(event: PlaidOnSuccessArgs, metadata: PlaidSuccessMetadata) {
		await this.fetchIdentityVerificationDetails(null);

		console.log('On Success is called ', event.token);
		if (this.userIdentityVerificationStatus == UserIdentityVerificationStatus.NotCreated) {
			// const identityVerificationStatus = await this.getIdentityVerificationStatus();
			// if (identityVerificationStatus) {
			// 	const userIdentityVerifcationStatus = identityVerificationStatus;
			// 	if (userIdentityVerifcationStatus) {
			// 		const test = userIdentityVerifcationStatus as UserIdentityVerificationStatus;
			// 		if (test) {
			// 			this.userIdentityVerificationStatus = test;
			// 		}
			// 	}
			// }
		}
		// this.config.onUserIdentityVerification(this.userIdentityVerificationStatus);
		console.log('Get Identity Verification list', this.userIdentityVerificationStatus);
	}
	async onEvent(event: any, metadata: PlaidEventMetadata) {
		console.log('We got an event and metadata', event, metadata);
		if (event == 'IDENTITY_VERIFICATION_CLOSE_UI') {
			const identityVerificationResponse = await this.fetchIdentityVerificationDetails(event);
			console.log('Identity Verification fetched from plaid', identityVerificationResponse);
			this.plaidOutput.emit(identityVerificationResponse);
		}
	}
	async onExit(error: any, metadata: any) {
		await this.fetchIdentityVerificationDetails(null);
		console.log('We exited:', error);
	}

	async fetchIdentityVerificationDetails(event: any): Promise<IdentityVerificationItemResponse> {
		try {
			const listUserIdentityVerificationRequest: ListUserIdentityVerificationRequest = {
				userId: this.plaidClientUserID,
			};
			const identityVerificationResponse = await lastValueFrom(this.apiV2.identityVerificationList({ body: listUserIdentityVerificationRequest }));
			if (!identityVerificationResponse.isSuccess || !identityVerificationResponse.data?.identityVerification) throw new Error('Error fetching Identity Verification for the given user');
			return identityVerificationResponse.data;
		} catch (error: any) {
			throw error;
		}
	}
}

interface PlaidApiConfig {
	environment: string;
	clientId: string;
	clientSecret: string;
	appVersion: string;
	verificationTemplateKey: string;
	products: string[];
	retryStrategy: string;
	country_codes: CountryCodes[];
	dateFormat: 'YYYY-MM-DD';
	alwaysCreateNewIddentityVerificationTemplate: boolean;
}

interface CountryCodes {
	country_code: string;
	country_calling_code: string;
}
